import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Type, Edit3, RotateCcw, Check } from 'lucide-react';

/**
 * SignaturePad — luxury hybrid type/draw signature capture.
 *
 * Props:
 *   - clientName: initial value for the "type" mode input
 *   - onChange: ({ signerName, signatureImage, signatureMethod }) => void
 *   - disabled: boolean
 */
export default function SignaturePad({ clientName = '', onChange, disabled = false }) {
  const [mode, setMode] = useState('type'); // 'type' | 'draw'
  const [typed, setTyped] = useState(clientName || '');
  const [drawnName, setDrawnName] = useState(clientName || '');
  const [hasDrawing, setHasDrawing] = useState(false);

  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const isDrawingRef = useRef(false);
  const hasDrawingRef = useRef(false);
  const lastPtRef = useRef({ x: 0, y: 0 });

  // Initialise the canvas in 'draw' mode AND keep it sized to the rendered box.
  // The backing store is the CSS size × devicePixelRatio so strokes stay crisp
  // at any width. On a viewport change we re-size WITHOUT wiping a signature the
  // client already drew — the current bitmap is captured and redrawn. No
  // animation here, so nothing to gate on prefers-reduced-motion.
  useEffect(() => {
    if (mode !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const setup = (preserve) => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0) return;
      const dpr = window.devicePixelRatio || 1;
      const prev = preserve && hasDrawingRef.current ? canvas.toDataURL('image/png') : null;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#0A0E18';
      ctx.lineWidth = 2.4;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, rect.width, rect.height);
      ctxRef.current = ctx;
      if (prev) {
        const img = new Image();
        img.onload = () => { ctx.drawImage(img, 0, 0, rect.width, rect.height); };
        img.src = prev;
      }
    };

    setup(false);
    const onResize = () => setup(true);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [mode]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0] || e.changedTouches?.[0];
    const clientX = touch ? touch.clientX : e.clientX;
    const clientY = touch ? touch.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    if (disabled) return;
    e.preventDefault();
    isDrawingRef.current = true;
    lastPtRef.current = getPoint(e);
  };

  const draw = (e) => {
    if (!isDrawingRef.current || disabled) return;
    e.preventDefault();
    const p = getPoint(e);
    const ctx = ctxRef.current;
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPtRef.current = p;
    hasDrawingRef.current = true;
    setHasDrawing(true);
  };

  const endDraw = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    emitChange();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasDrawingRef.current = false;
    setHasDrawing(false);
    // Immediately propagate cleared state
    if (onChange) onChange({ signerName: drawnName.trim(), signatureImage: '', signatureMethod: 'draw' });
  };

  const emitChange = useCallback(() => {
    if (!onChange) return;
    if (mode === 'type') {
      const name = typed.trim();
      if (!name) { onChange({ signerName: '', signatureImage: '', signatureMethod: 'type' }); return; }
      // Render cursive preview to a small offscreen canvas → PNG data URI so the PDF shows the actual script
      const c = document.createElement('canvas');
      c.width = 800; c.height = 200;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height);
      ctx.fillStyle = '#0A0E18';
      ctx.font = 'italic 88px "Dancing Script", "Brush Script MT", cursive';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillText(name, c.width / 2, c.height / 2);
      onChange({ signerName: name, signatureImage: c.toDataURL('image/png'), signatureMethod: 'type' });
    } else {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const signatureImage = hasDrawing ? canvas.toDataURL('image/png') : '';
      onChange({ signerName: drawnName.trim(), signatureImage, signatureMethod: 'draw' });
    }
  }, [mode, typed, drawnName, hasDrawing, onChange]);

  // Emit whenever typed or drawnName or mode changes
  useEffect(() => {
    emitChange();
     
  }, [typed, drawnName, mode]);

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="signature-pad">
      {/* Mode tabs */}
      <div className="flex gap-0 px-1 pt-1" style={{ background: 'rgba(0,0,0,0.18)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <button
          type="button"
          onClick={() => setMode('type')}
          disabled={disabled}
          className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold tracking-wide transition-all rounded-t-lg"
          style={{
            background: mode === 'type' ? 'rgba(212,175,55,0.1)' : 'transparent',
            color: mode === 'type' ? '#D4AF37' : 'rgba(255,255,255,0.4)',
            borderBottom: mode === 'type' ? '2px solid rgba(212,175,55,0.5)' : '2px solid transparent',
          }}
          data-testid="sig-mode-type"
        >
          <Type className="w-3.5 h-3.5" /> Type
        </button>
        <button
          type="button"
          onClick={() => setMode('draw')}
          disabled={disabled}
          className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold tracking-wide transition-all rounded-t-lg"
          style={{
            background: mode === 'draw' ? 'rgba(212,175,55,0.1)' : 'transparent',
            color: mode === 'draw' ? '#D4AF37' : 'rgba(255,255,255,0.4)',
            borderBottom: mode === 'draw' ? '2px solid rgba(212,175,55,0.5)' : '2px solid transparent',
          }}
          data-testid="sig-mode-draw"
        >
          <Edit3 className="w-3.5 h-3.5" /> Draw
        </button>
      </div>

      {/* Body — at phone width this card does NOT re-add its own side gutter;
          the page gutter (main px-4) is the single gutter. Vertical padding
          stays; desktop (sm:) is unchanged. */}
      <div className="px-0 sm:px-4 py-4">
        {mode === 'type' ? (
          <>
            <label className="text-[10px] text-white/25 tracking-widest uppercase font-medium mb-1.5 block">Type your full legal name</label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="e.g. John Smith"
              disabled={disabled}
              className="w-full px-4 py-2.5 rounded-xl text-[14px] text-white/80 bg-transparent outline-none placeholder:text-white/15 focus:border-[#D4AF37]/30 transition-colors"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              data-testid="sig-type-input"
            />

            {/* Live cursive preview */}
            <div className="mt-3 rounded-xl flex items-center justify-center" style={{ background: '#ffffff', minHeight: '100px', padding: '18px' }} data-testid="sig-type-preview">
              {typed.trim() ? (
                <span style={{ fontFamily: '"Dancing Script", "Brush Script MT", cursive', fontSize: '44px', color: '#0A0E18', fontStyle: 'italic', textAlign: 'center', lineHeight: 1 }}>
                  {typed}
                </span>
              ) : (
                <span className="text-[12px]" style={{ color: '#9ca3af', fontStyle: 'italic' }}>Your signature will appear here</span>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <label className="text-[10px] text-white/25 tracking-widest uppercase font-medium">Draw your signature</label>
              <button
                type="button"
                onClick={clearCanvas}
                disabled={disabled || !hasDrawing}
                className="flex items-center justify-center gap-1 px-2 min-h-[44px] sm:min-h-0 text-[10px] text-white/30 hover:text-white/60 disabled:opacity-30 transition"
                data-testid="sig-clear-btn"
              >
                <RotateCcw className="w-3 h-3" /> Clear
              </button>
            </div>
            <div style={{ background: '#ffffff', borderRadius: '12px', padding: '4px' }}>
              <canvas
                ref={canvasRef}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
                style={{ width: '100%', height: '180px', minHeight: '140px', touchAction: 'none', cursor: 'crosshair', display: 'block' }}
                data-testid="sig-canvas"
              />
            </div>

            {/* Name field to accompany the drawn signature */}
            <label className="text-[10px] text-white/25 tracking-widest uppercase font-medium mb-1.5 mt-3 block">Printed Name</label>
            <input
              type="text"
              value={drawnName}
              onChange={(e) => setDrawnName(e.target.value)}
              placeholder="Type your full legal name"
              disabled={disabled}
              className="w-full px-4 py-2.5 rounded-xl text-[13px] text-white/80 bg-transparent outline-none placeholder:text-white/15 focus:border-[#D4AF37]/30 transition-colors"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              data-testid="sig-draw-name-input"
            />

            {hasDrawing && (
              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[#22C55E]/70">
                <Check className="w-3 h-3" /> Signature captured
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
